import {ChoiceKey, ChoiceModel} from 'models/configuration/ChoiceModel'
import {OptionKey, OptionModel} from 'models/configuration/OptionModel'
import {PackKey, PackModel} from 'models/configuration/PackModel'
import {TourKey, TourModel} from 'models/configuration/TourModel'
import {TrackModel} from 'models/configuration/TrackModel'
import {EventModel} from 'models/EventModel'
import {StepConfig, StepKey, StepModel} from 'models/registration/StepModel'
import {BadeStepModel} from 'models/registration/steps/BadgeStepModel'
import {
	ConfigureStepConfig,
	ConfigureStepModel
} from 'models/registration/steps/ConfigureStepModel'
import {
	DetailsStepConfig,
	DetailsStepModel,
	DetailsStepState,
	RegistrationStatus
} from 'models/registration/steps/DetailsStepModel'
import {
	OnsiteStepConfig,
	OnsiteStepModel
} from 'models/registration/steps/OnsiteStepModel'
import {
	SelectStepConfig,
	SelectStepModel
} from 'models/registration/steps/SelectStepModel'
import {
	ConfirmationData,
	SummaryStepConfig,
	SummaryStepModel,
	SummaryStepState
} from 'models/registration/steps/SummaryStepModel'
import {
	TouringStepConfig,
	TouringStepModel
} from 'models/registration/steps/TouringStepModel'
import {Memoize} from 'util/memoize'
import {LinkType} from '../../types'
import {
	GiftStepConfig,
	GiftStepModel,
	GiftStepState
} from './steps/GiftStepModel'

export type CountryLabel = string
export type CountryCode = string

export type RegistrationConfig = {
	id: number
	url: string
	eventID: number
	channel: 'registration' | 'registration_onsite'
	invite_only: boolean
	no_kp: boolean

	select: SelectStepConfig
	breakouts: ConfigureStepConfig
	configure: ConfigureStepConfig
	details: DetailsStepConfig
	gift: GiftStepConfig
	touring: TouringStepConfig
	summary: SummaryStepConfig
	onsite: OnsiteStepConfig
	badge: StepConfig

	back_links: Array<LinkType>
	contact_link: LinkType
	general: {
		need_help: string
		back_btn: string
		next_btn: string
	}

	eloqua_post: string
	countries: Record<CountryCode, CountryLabel>
}

export type OptionsState = Record<OptionKey, ChoiceKey | undefined>
export type CommentsState = Record<OptionKey, string>

export type RegistrationState = {
	created_stamp: string
	packs: Array<PackKey>
	options: OptionsState
	comments?: CommentsState
	details: DetailsStepState
	tours: Array<TourKey>
	gift: GiftStepState
	reference: string
	reference_description?: string
	summary: SummaryStepState
	confirmation?: ConfirmationData
	tags: Record<string, boolean>
	type: string
	validated: boolean
}

const defaultState: RegistrationState = {
	created_stamp: new Date().toISOString(),
	packs: [],
	options: {},
	tours: [],
	details: {},
	gift: {},
	summary: {},
	reference: null,
	reference_description: null,
	tags: {},
	type: null,
	validated: false
}

export class RegistrationModel {
	config: RegistrationConfig
	event: EventModel
	state: RegistrationState

	constructor(
		config: RegistrationConfig,
		event: EventModel,
		state?: RegistrationState
	) {
		this.config = config
		this.event = event
		this.state = state || defaultState
		if (!state) {
			const defaultPacks = this.event.packs.filter(pack => pack.isPreselected)
			const defaultTags = [
				...new Set(...defaultPacks.map(pack => pack.tagsSet))
			]
			this.state = {
				...defaultState,
				packs: defaultPacks.map(pack => pack.key),
				tags: defaultTags.reduce((acc, curr) => ({...acc, [curr]: true}), {})
			}
		}

		// Cleanup unused options & packs
		this.state = {
			...this.state,
			details: this.allSteps.details.state,
			packs: this.packs.map(p => p.key),
			options: this.options.reduce(
				(state: OptionsState, option: OptionModel) => {
					const choice = option.getChoiceFor(this)
					if (!choice) return state
					return {
						...state,
						[option.key]: choice.key
					}
				},
				{} as OptionsState
			),
			tours: this.tours
				.filter(t => !t.hasConflictingToursFor(this))
				.map(t => t.key)
		}
	}

	@Memoize()
	get isOnsite(): boolean {
		return this.config.channel === 'registration_onsite'
	}

	@Memoize()
	get isOnline(): boolean {
		return !!this.packs.find(pack => pack.isOnline)
	}

	@Memoize()
	get backLinks(): Array<LinkType> {
		return this.config.back_links
	}

	@Memoize()
	get contactLink(): LinkType {
		return this.config.contact_link
	}

	@Memoize()
	get confirmation(): ConfirmationData {
		return this.state.confirmation
	}

	@Memoize()
	get name(): string {
		const details = this.allSteps.details
		return `${details.state.firstname} ${details.state.lastname}`
	}

	@Memoize()
	get id(): number {
		return this.config.id
	}

	@Memoize()
	get packs(): Array<PackModel> {
		const packKeys = new Set(this.state.packs)
		return this.event.packs.filter(pack => packKeys.has(pack.key))
	}

	@Memoize()
	get tours(): Array<TourModel> {
		const tourKeys = new Set(this.state.tours)
		return this.event.tours.filter(tour => tourKeys.has(tour.key))
	}

	@Memoize()
	get tracks(): Array<TrackModel> {
		let tracks: Array<TrackModel> = []
		this.packs.forEach(pack => {
			pack.tracks.forEach(track => {
				if (!new Set(tracks).has(track)) tracks = [...tracks, track]
			})
		})
		return tracks
	}

	@Memoize()
	get options(): Array<OptionModel> {
		return this.event.options.filter(
			option =>
				option.tracks.find(track => this.tracksSet.has(track)) &&
				!option.skippedPackaged.find(pack => this.packsSet.has(pack))
		)
	}

	@Memoize()
	get choices(): Array<ChoiceModel> {
		const result = []
		this.options.forEach(option => {
			const choice = option.getChoiceFor(this)
			if (choice) result.push(choice)
		})
		return result
	}

	@Memoize()
	get packsSet(): Set<PackModel> {
		return new Set(this.packs)
	}

	@Memoize()
	get tracksSet(): Set<TrackModel> {
		return new Set(this.tracks)
	}

	@Memoize()
	get eventID(): number {
		return this.config.eventID
	}

	@Memoize()
	get email(): string {
		return this.allSteps.details.email
	}

	@Memoize()
	get country(): string {
		return this.allSteps.details.state.country
	}

	@Memoize()
	get company(): string {
		return this.allSteps.details.company
	}

	@Memoize()
	get isInviteOnly(): boolean {
		return this.config.invite_only
	}

	@Memoize()
	get isInvitee(): boolean {
		return this.status.type === 'invitee'
	}

	@Memoize()
	get isImecInvitee(): boolean {
		return this.status.type === 'invitee' && this.status.imec
	}

	@Memoize()
	get status(): RegistrationStatus {
		return this.state.details.status || {type: 'regular'}
	}

	@Memoize()
	get isConfirmedNotInvitee(): boolean {
		const status = this.state.details.status
		return status && status.type !== 'invitee'
	}

	@Memoize()
	get isAlreadyRegistered(): boolean {
		return this.state.details.status?.type === 'registered'
	}

	@Memoize()
	get countries(): Record<CountryCode, CountryLabel> {
		return this.config.countries
	}

	@Memoize()
	get sharedSelectedOptions(): Array<OptionModel> {
		return this.options.filter(
			option => option.tracks.length > 1 && option.getChoiceFor(this)
		)
	}

	@Memoize()
	get steps(): Array<StepModel> {
		const steps = []
		Object.keys(this.allSteps).map(key => {
			const step = this.allSteps[key]
			if (step && step.isApplicable) steps.push(step)
		})
		return steps
	}

	@Memoize()
	get allSteps() {
		return {
			select: new SelectStepModel(this, this.config.select),
			breakouts: new ConfigureStepModel(this, this.config.breakouts, true),
			configure: new ConfigureStepModel(this, this.config.configure, false),
			details: new DetailsStepModel(this, this.config.details),
			gift: new GiftStepModel(this, this.config.gift),
			touring: new TouringStepModel(this, this.config.touring),
			summary: new SummaryStepModel(this, this.config.summary),
			onsite: new OnsiteStepModel(this, this.config.onsite),
			badge: new BadeStepModel(this, this.config.badge)
		}
	}

	@Memoize()
	get totalPrice(): number {
		const packPrices = this.packs.reduce(
			(price, pack) => price + (pack.getPriceFor(this) || 0),
			0
		)
		const optionPrices = this.choices.reduce(
			(price, choice) => price + (choice.getPriceFor(this) || 0),
			0
		)
		return packPrices + optionPrices
	}

	@Memoize()
	get totalValue(): number {
		const packPrices = this.packs.reduce(
			(price, pack) => price + (pack.config.price || 0),
			0
		)
		const optionPrices = this.choices.reduce(
			(price, choice) => price + (choice.config.price || 0),
			0
		)
		return packPrices + optionPrices
	}

	@Memoize()
	get general() {
		return this.config.general
	}

	@Memoize()
	get isFreeEvent(): boolean {
		const packsNoPayment = this.packs.every(pack => !pack.config.has_payment)
		const optionsNoPayment = this.choices.every(
			choice => !choice.config.has_payment
		)
		return packsNoPayment && optionsNoPayment
	}

	getEloquaData(): Record<string, string> {
		const result: Record<string, string> = {}
		this.tracks.forEach(track => {
			result[track.key] = 'True'
		})
		this.event.options.forEach(option => {
			const choice = option.getChoiceFor(this)
			result[option.key] = choice?.key || ''

			if (option.choices.some(c => c.shouldIncludeComments)) {
				const commentsKey = `${option.key}_comments`
				result[commentsKey] = ''
				if (choice?.shouldIncludeComments) {
					result[commentsKey] = (this.state.comments || {})[commentsKey] || ''
				}
			}
		})

		this.event.packs.forEach(pack => {
			const packKeys = new Set(this.state.packs)
			result[`pack_${pack.key}`] = packKeys.has(pack.key) ? 'True' : 'False'
		})

		result.firstname = this.state.details.firstname
		result.lastname = this.state.details.lastname
		result.email = this.state.details.email
		result.kp = this.state.details.kp
		result.function = this.state.details.function
		result.company = this.state.details.company
		result.industry = this.state.details.industry
		result.country = this.state.details.country
		result.phone = this.state.details.phone
		result.vat = this.state.details.vat
		result.street = this.state.details.street
		result.number = this.state.details.number
		result.box = this.state.details.box
		result.zipcode = this.state.details.postal_code
		result.city = this.state.details.city
		result.state = this.state.details.state
		result.website = this.state.details.website

		result.invitee = this.isInvitee ? 'True' : 'False'
		result.free_options =
			this.status?.type === 'invitee' && this.status.free_options
				? 'True'
				: 'False'

		result.agree_terms = 'True'
		result.agree_privacy = 'True'

		return result
	}

	confirmStepKey(key: StepKey): StepKey {
		for (let i = 0; i < this.steps.length; i++) {
			const step = this.steps[i]
			if (key === step.key) return key
			if (!step.isReady) return step.key
		}
		return this.steps[this.steps.length - 1].key
	}

	// ----- State changes ----- //
	update(newState: Partial<RegistrationState>): RegistrationModel {
		return new RegistrationModel(this.config, this.event, {
			...this.state,
			...newState
		})
	}

	clear(): RegistrationModel {
		return new RegistrationModel(this.config, this.event, defaultState)
	}
}
